import React, { useRef, useState, useEffect } from 'react';
import { Space, Typography, Popconfirm, Modal, InputNumber, message } from 'antd';
import { DeleteOutlined, DashboardOutlined, AppstoreOutlined } from '@ant-design/icons';
import { Tournament } from '@/types/api.types';
import { HeaderBanner } from '@/components/common/HeaderBanner';
import { ActionButton } from '@/components/common/ActionButton';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { TournamentService } from '@/services/tournament.service';
import { logger } from '@/utils/logger';
import { ActionButtons } from './styles/TournamentHeader.styles';

const { Title } = Typography;


interface TournamentHeaderProps {
  tournament: Tournament;
  onEdit?: () => void;
  onDelete?: () => void;
  onRegisterSuccess?: () => void;
}

export const TournamentHeader: React.FC<TournamentHeaderProps> = ({
  tournament,
  onDelete,
  onEdit: _onEdit,
  onRegisterSuccess: _onRegisterSuccess
}) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const headerRef = useRef<HTMLDivElement>(null);
  const [isGenerateBracketsModalVisible, setIsGenerateBracketsModalVisible] = useState(false);
  const [generatingBrackets, setGeneratingBrackets] = useState(false);
  const [numTatami, setNumTatami] = useState<number>(1);
  const [hasMatches, setHasMatches] = useState<boolean>(false);
  const [checkingMatches, setCheckingMatches] = useState(true);

  // Проверяем права на управление турниром: админ, создатель или организатор
  const canManageTournament = user?.role === 'admin' || 
    user?.id === tournament.createdById || 
    user?.id === tournament.organizerId ||
    (user?.role === 'manager' && tournament.clubId && user.clubId === tournament.clubId) ||
    (user?.role === 'manager' && tournament.clubId && user.clubId === tournament.clubId);

  const showScoreboardButton = tournament.status === 'in_progress' || tournament.status === 'completed';

  // Проверяем наличие матчей для турнира
  useEffect(() => {
    const checkMatches = async () => {
      if (!tournament?.id) return;
      
      try {
        setCheckingMatches(true);
        const matches = await TournamentService.getTournamentMatches(tournament.id);
        const matchesArray = Array.isArray(matches) 
          ? matches 
          : (matches as any)?.data || [];
        setHasMatches(matchesArray.length > 0);
      } catch (error) {
        logger.error('Error checking matches:', error);
        setHasMatches(false);
      } finally {
        setCheckingMatches(false);
      }
    };

    checkMatches();
  }, [tournament?.id]);

  // Кнопка "Сформировать пулы" доступна только если матчи еще не были сформированы
  // Проверка доступа: админы, организаторы турнира или менеджеры/тренеры клуба-организатора могут формировать пули
  const isAdmin = user?.role === 'admin';
  const isOrganizer = tournament.organizerId === user?.id;
  const isManagerOrCoach = user?.role === 'manager' || user?.role === 'coach' || user?.role === 'head_manager';
  const isTournamentClubMember = tournament.clubId && user?.clubId && tournament.clubId === user.clubId;
  
  // Кнопка доступна, если есть участники, турнир опубликован и нет записей в таблице боев
  const canGenerateBrackets = (isAdmin || isOrganizer || (isManagerOrCoach && isTournamentClubMember)) &&
    tournament.status === 'published' &&
    !checkingMatches &&
    (tournament.participantsCount || 0) > 0 &&
    !hasMatches;

  const handleGenerateBrackets = async () => {
    if (!tournament?.id) return;

    console.group('🚀 [TournamentHeader] Начало генерации пулов');
    console.log('📋 Данные турнира:', {
      id: tournament.id,
      name: tournament.name,
      status: tournament.status,
      numTatami
    });

    try {
      setGeneratingBrackets(true);
      const result = await TournamentService.generateBrackets(tournament.id, numTatami);
      console.log('✅ Результат генерации:', result);
      message.success('Пулы успешно сформированы');
      setIsGenerateBracketsModalVisible(false);
      console.groupEnd();
      // Не перезагружаем страницу; сетки будут доступны на вкладке "Сетки"/"Жеребьевка".
    } catch (error: any) {
      console.error('❌ [TournamentHeader] Ошибка при генерации пулов:', error);
      console.error('Детали ошибки:', {
        message: error?.message,
        response: error?.response?.data,
        status: error?.response?.status
      });
      console.groupEnd();
      logger.error('Error generating brackets:', error);
      message.error(error?.response?.data?.message || error?.message || 'Ошибка при формировании пулов');
    } finally {
      setGeneratingBrackets(false);
    }
  };

  return (
    <>
      <HeaderBanner ref={headerRef} coverImage={tournament.coverImage}>
        <Space direction="vertical" size="small" style={{ width: '100%', background: 'transparent' }}>
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            width: '100%',
            background: 'transparent',
            padding: 0,
          }}>
            <Title 
              level={2} 
              className="tournament-title-no-bg"
              style={{ 
                color: 'white', 
                margin: 0,
                background: 'transparent',
                backgroundColor: 'transparent',
                maxWidth: '80%',
                textAlign: 'center',
                wordWrap: 'break-word',
                overflowWrap: 'break-word',
                whiteSpace: 'normal',
                lineHeight: '1.4',
                padding: 0,
              }}
            >
              {tournament.name}
            </Title>
          </div>
          <style>{`
            .tournament-title-no-bg,
            .tournament-title-no-bg h2,
            .tournament-title-no-bg .ant-typography,
            .tournament-title-no-bg h2.ant-typography {
              background: transparent !important;
              background-color: transparent !important;
              padding: 0 !important;
              margin: 0 !important;
            }
          `}</style>
          <ActionButtons>
            {showScoreboardButton && (
              <ActionButton
                type="primary"
                icon={<DashboardOutlined />}
                onClick={() => navigate(`/tournaments/${tournament.id}/scoreboard`)}
                style={{
                  backgroundColor: '#ff4d4f',
                  borderColor: '#ff4d4f',
                  color: 'white',
                }}
              >
                Live Табло
              </ActionButton>
            )}

            {canGenerateBrackets && (
              <ActionButton
                type="primary"
                icon={<AppstoreOutlined />}
                onClick={() => setIsGenerateBracketsModalVisible(true)}
                style={{
                  backgroundColor: '#fa8c16',
                  borderColor: '#fa8c16',
                  color: 'white'
                }}
              >
                Сформировать пули
              </ActionButton>
            )}

            {canManageTournament && onDelete && (
              <Popconfirm
                title="Удаление турнира"
                description={`Вы уверены, что хотите удалить турнир "${tournament.name}"?`}
                onConfirm={onDelete}
                okText="Да, удалить"
                cancelText="Отмена"
                okButtonProps={{ danger: true }}
              >
                <ActionButton
                  type="primary"
                  danger
                  icon={<DeleteOutlined />}
                  style={{
                    backgroundColor: '#ff4d4f',
                    borderColor: '#ff4d4f',
                    color: 'white'
                  }}
                >
                  Удалить турнир
                </ActionButton>
              </Popconfirm>
            )}
          </ActionButtons>
        </Space>
      </HeaderBanner>

      <Modal
        title="Сформировать пулы"
        open={isGenerateBracketsModalVisible}
        onOk={handleGenerateBrackets}
        onCancel={() => setIsGenerateBracketsModalVisible(false)}
        confirmLoading={generatingBrackets}
        okText="Сформировать"
        cancelText="Отмена"
      >
        <p>Вы уверены, что хотите сформировать пулы для этого турнира?</p>
        <p>Это действие распределит всех зарегистрированных участников по пулам в соответствии с их категориями.</p>
        <p>При формировании пулов будут учтены следующие условия:</p>
        <ul>
          <li>Максимально развести бойцов от одного тренера в разные ветки</li>
          <li>Максимально развести бойцов с одного клуба</li>
          <li>Максимально развести бойцов с одного города</li>
          <li>Максимально развести бойцов с одной федерации</li>
        </ul>
        <div style={{ marginTop: '16px' }}>
          <label>Количество татами:</label>
          <InputNumber
            min={1}
            max={10}
            value={numTatami}
            onChange={(value) => setNumTatami(value || 1)}
            style={{ width: '100%', marginTop: '8px' }}
          />
        </div>
        <p style={{ marginTop: '16px', color: '#ff4d4f', fontWeight: 'bold' }}>
          Внимание: Пулы формируются один раз. После формирования вы сможете редактировать сетку при помощи drag-and-drop.
        </p>
      </Modal>
    </>
  );
};